import { useTranslation } from "@privacy/translations";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import type { ReplaceResponsibleSelection } from "../../../../api/users";
import {
  ReplaceResponsibleErrorCodes,
  replaceResponsibleCandidatesQueryOptions,
  useReplaceResponsible,
} from "../../../../api/users";
import { hasErrorCode } from "../../../../api/errors";
import { UserSelect } from "../../../../shared/UserSelect";
import * as FormDialog from "../../../../shared/FormDialog";
import type { ResponsibleEntitiesKey } from "../constants";

type ChangeResponsibleDialogProps = {
  onClose: () => void;
  onReplaced: () => void;
  selectedEntityIds: Array<string>;
  currentResponsibleUserId: string;
  responsibleEntitiesKey: ResponsibleEntitiesKey;
};

const entityKeyToPayloadKey: Record<ResponsibleEntitiesKey, keyof ReplaceResponsibleSelection> = {
  registrationsWhereResponsible: "registrationIds",
  processorRegistrationsWhereResponsible: "processorRegistrationIds",
  dpiasWhereResponsible: "dpiaIds",
  systemsWhereResponsible: "systemIds",
};

export const ChangeResponsibleDialog = ({
  onClose,
  onReplaced,
  selectedEntityIds,
  currentResponsibleUserId,
  responsibleEntitiesKey,
}: ChangeResponsibleDialogProps) => {
  const { t, formatList } = useTranslation();
  const selection: ReplaceResponsibleSelection = { [entityKeyToPayloadKey[responsibleEntitiesKey]]: selectedEntityIds };
  const candidatesQuery = useQuery(replaceResponsibleCandidatesQueryOptions(currentResponsibleUserId, selection));
  const replaceResponsibleMutation = useReplaceResponsible(currentResponsibleUserId);
  const replaceToUserLacksPermission = hasErrorCode(
    replaceResponsibleMutation.error,
    ReplaceResponsibleErrorCodes.replaceToUserHasNoPermissionOnAllTenants
  );
  const { control, handleSubmit } = useForm<{ replaceToUserId: string }>({ defaultValues: { replaceToUserId: "" } });

  return (
    <FormDialog.Dialog isOpen>
      <FormDialog.Form
        onSubmit={handleSubmit(({ replaceToUserId }) =>
          replaceResponsibleMutation.mutate({ replaceToUserId, ...selection }, { onSuccess: onReplaced })
        )}
      >
        <FormDialog.Title>
          {t(
            `User.Grid.Drawer.ChangeResponsibleDialog.${responsibleEntitiesKey}.Title${selectedEntityIds.length > 1 ? "Plural" : "Singular"}`,
            { numberOfEntities: selectedEntityIds.length }
          )}
        </FormDialog.Title>
        <FormDialog.Content>
          <FormDialog.FormError
            isError={replaceResponsibleMutation.isError}
            errorMessage={
              replaceToUserLacksPermission
                ? t("User.Grid.Drawer.ChangeResponsibleDialog.ReplaceToUserHasNoPermissionError")
                : undefined
            }
          />

          <Typography
            sx={{
              pb: 2,
              fontSize: theme => theme.typography.pxToRem(14),
              color: theme => theme.palette.text.secondary,
            }}
          >
            {t(
              `User.Grid.Drawer.ChangeResponsibleDialog.${responsibleEntitiesKey}.Body${selectedEntityIds.length > 1 ? "Plural" : "Singular"}`
            )}
          </Typography>
          <FormDialog.Fields>
            <FormDialog.ControlledField
              control={control}
              name="replaceToUserId"
              rules={{ required: t("Input.Validation.Required") }}
              label={t(`User.Grid.Drawer.ChangeResponsibleDialog.UserSelectLabel`)}
            >
              {({ field: { ref, onChange, value }, fieldState, id }) => (
                <UserSelect
                  id={id}
                  error={Boolean(fieldState.error)}
                  selectedId={value}
                  users={candidatesQuery.data?.users}
                  getDisabledReason={candidate =>
                    candidate.canBeResponsible
                      ? undefined
                      : t("User.Grid.Drawer.ChangeResponsibleDialog.MissingCreatePermissionTooltip", {
                          units: formatList(candidate.tenantsWhereCannotBeResponsible.map(tenant => tenant.name)),
                        })
                  }
                  ref={ref}
                  onSelectUser={onChange}
                />
              )}
            </FormDialog.ControlledField>
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={replaceResponsibleMutation.isPending}>
            {t(`User.Grid.Drawer.ChangeResponsibleDialog.ConfirmButton`)}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
